'use client';

import { useState } from 'react';

import { PopImage } from '@ps/ui/atoms/pop-image';
import { YoutubeIcon } from '@ps/ui/icons/YoutubeIcon';
import { YoutubePlayIcon } from '@ps/ui/icons/YoutubePlayIcon';

import { CustomButtonContainer } from '../../../common/components/CustomButtonContainer';
import { buildYoutubeEmbed } from '../../../functions/youtube';
import type { VideoButtonViewModel } from '../types/VideoButtonViewModel';

// Legacy `video.tsx` paints a solid black backdrop behind the embed / facade /
// placeholder so YouTube's letterboxing and the placeholder icon read against a
// consistent surface. Not a theme color — an embed-cropping constant.
const kVideoBackdrop = '#000';
// YouTube brand red — matches the social-links icon background (`iconBackgroundFor`).
const kYoutubeRed = '#FF0000';

interface Props {
  viewModel: VideoButtonViewModel;
  className?: string;
}

/**
 * The video custom button — faithful port of legacy
 * `surfaces/custom-buttons/components/variants/VideoButton.tsx`.
 *
 * It's a `'use client'` component (it needs the click-to-play toggle state) but
 * is SERVER-RENDERED — statically imported by the dispatch, NOT an `ssr: false`
 * island — so its lightweight facade (a static YouTube thumbnail + play overlay)
 * paints at first load with no pop-in. The heavy part is the iframe, and that's
 * browser-loaded, not a JS bundle: until the visitor taps the play overlay we
 * render only the thumbnail — no iframe, no YouTube preconnects, no Roboto font
 * request, no ~250 KB player. Lighthouse audits the initial HTML, so as long as
 * the iframe doesn't exist at first paint those audit items clear ("More than 4
 * preconnect connections", "Font display" for Roboto, "Improve image delivery"
 * from YouTube's eager thumbnails). Once `mounted` flips true the real iframe
 * (autoplay — the visitor explicitly asked for the video) takes over.
 *
 * No title/subtitle overlay (legacy commented out `<CustomButtonContent>` for
 * video). A null `videoId` (missing URL / non-YouTube host) renders the
 * placeholder Video icon — only YouTube embeds are supported, matching legacy
 * which has no `<video>` fallback either.
 */
export function VideoButtonView({ viewModel, className }: Props) {
  const { buttonStyle, videoId, zoom, title } = viewModel;

  // Click-to-play facade state. Flips true on the first tap of the play
  // overlay; the iframe is mounted (and autoplays) only after that.
  const [mounted, setMounted] = useState(false);

  if (videoId == null) {
    return (
      <CustomButtonContainer buttonStyle={buttonStyle} className={className}>
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: kVideoBackdrop, color: kYoutubeRed }}
        >
          <YoutubeIcon className="h-7 w-7" />
        </div>
      </CustomButtonContainer>
    );
  }

  const embed = buildYoutubeEmbed(videoId, {
    // Auto-play once the visitor has explicitly clicked the facade — they
    // wanted the video, so no second tap.
    autoplay: mounted,
    controls: true,
    mute: false,
    showInfo: false,
    modestBranding: false,
    rel: false,
    zoom,
  });

  if (mounted) {
    return (
      <CustomButtonContainer buttonStyle={buttonStyle} className={className}>
        {/* Two-element embed (1:1 legacy): the wrapper carries the zoom crop,
            the iframe fills it via the `width:0; min-width:100%` trick so iOS
            Safari stretches it to full width (QA: video rendered small on iOS
            when the iframe used a percentage width directly). */}
        <div style={{ ...embed.wrapperStyle, backgroundColor: kVideoBackdrop }}>
          <iframe
            loading="lazy"
            src={embed.src}
            style={embed.iframeStyle}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            title={title}
          />
        </div>
      </CustomButtonContainer>
    );
  }

  // YouTube serves predictable static thumbnails; `hqdefault` (480x360) is small
  // yet large enough for the button area. PopImage renders it with NO `resizing`,
  // so it's served straight from YouTube's CDN (already the right size — routing
  // it through our resize CDN would only add a hop) and never the `/_next/image`
  // optimizer (which would need `i.ytimg.com` in remotePatterns). No BlurHash on
  // a YouTube URL; PopImage just unifies the image code path.
  const thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <CustomButtonContainer buttonStyle={buttonStyle} className={className}>
      <button
        type="button"
        onClick={() => setMounted(true)}
        aria-label={`Play ${title}`}
        className="group absolute inset-0 cursor-pointer overflow-hidden"
        style={{ backgroundColor: kVideoBackdrop }}
      >
        <PopImage
          src={thumbnailUrl}
          alt=""
          fill
          loading="lazy"
          sizes="(min-width: 512px) 480px, 100vw"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition group-hover:bg-black/30">
          {/* The official YouTube play mark (red rounded-rect + SOLID white
              triangle), drop-shadowed for legibility over the thumbnail. Uses
              `YoutubePlayIcon`, NOT the plain `YoutubeIcon` logo — the logo's
              triangle is a transparent cutout, so over the dark backdrop it
              reads as a hole instead of a white play arrow. */}
          <div
            className="drop-shadow-lg transition group-hover:scale-110"
            style={{ color: kYoutubeRed }}
          >
            <YoutubePlayIcon className="h-12 w-12" />
          </div>
        </div>
      </button>
    </CustomButtonContainer>
  );
}
